/**
 * `/audience/participants/:id` — one profile across platforms (§12.5), masked by default (§16.3); unmasking
 * goes through `MaskedCell` (purpose + step-up + audit). Withdrawal handling and the §17.3 erase workflow
 * (typed confirmation, legal holds respected, suppression kept).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Skeleton, useIntl, useToast } from '@windrop/ui';
import { useState } from 'react';
import { useParams } from 'react-router';
import { Can } from '../../auth/guards';
import { StepUpCancelled, useStepUp } from '../../auth/step-up';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { ErrorState } from '../../components/EmptyState';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { audienceApi } from '../../lib/api/analytics';
import { isApiError } from '../../lib/api/client';

export function ParticipantProfilePage() {
  const { id = '' } = useParams();
  const intl = useIntl();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { withStepUp } = useStepUp();
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const query = useQuery({
    queryKey: ['audience', 'participant', id],
    queryFn: () => audienceApi.profile(id),
    enabled: Boolean(id),
  });

  const withdraw = useMutation({
    mutationFn: (reason: string) => audienceApi.withdraw(id, { purpose: 'MARKETING', reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['audience'] });
      toast.toast({
        title: 'Marketing consent withdrawn',
        description: 'Future exports are suppressed; operational winner messages remain separate.',
        tone: 'success',
      });
      setWithdrawOpen(false);
    },
  });
  const erase = useMutation({
    mutationFn: (reason: string) =>
      withStepUp(() => audienceApi.erase(id, { confirmParticipantId: id, reason }), {
        permission: 'audience.export',
        reason: `Erasing participant ${id}`,
      }),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['audience'] });
      toast.toast({
        title: r.status === 'COMPLETED' ? 'Profile erased' : 'Erasure blocked by a legal hold',
        description:
          r.status === 'COMPLETED'
            ? `${r.erased.channelIdentities} channels and ${r.erased.mediaObjects} media objects removed; the suppression record is kept.`
            : `Holds: ${r.legalHoldRefs.join(', ')}`,
        tone: r.status === 'COMPLETED' ? 'success' : 'neutral',
      });
      setEraseOpen(false);
    },
    onError: (e) => {
      if (e instanceof StepUpCancelled) setEraseOpen(false);
    },
  });

  if (query.isPending) return <Skeleton shape="block" height={240} label="Loading participant" />;
  if (query.isError || !query.data)
    return (
      <ErrorState
        title="Participant not found"
        body="It may belong to another tenant or have been erased."
        reference={isApiError(query.error) ? query.error.supportReference : undefined}
        action={<Button href="/audience">Back to audience</Button>}
      />
    );
  const p = query.data;
  const marketing = p.consents.marketing
    ? 'OPTED_IN'
    : p.consents.marketingWithdrawnAt
      ? 'WITHDRAWN'
      : 'NONE';
  return (
    <>
      <PageHeader
        breadcrumb={[{ label: 'Audience', to: '/audience' }, { label: p.displayName }]}
        title={p.displayName}
        meta={
          <ToneBadge
            tone={marketing === 'OPTED_IN' ? 'success' : marketing === 'WITHDRAWN' ? 'warning' : 'neutral'}
          >
            Marketing {humanizeStatus(marketing).toLowerCase()}
          </ToneBadge>
        }
        description={`${p.lotteriesEntered} lotteries entered · ${p.prizesWon} prizes won · first seen ${intl.formatDateTime(p.createdAt)}`}
        actions={
          <Can permission="audience.export">
            {marketing === 'OPTED_IN' ? (
              <Button variant="secondary" onClick={() => setWithdrawOpen(true)}>
                Record withdrawal
              </Button>
            ) : null}
            <Button variant="danger" onClick={() => setEraseOpen(true)}>
              Erase profile
            </Button>
          </Can>
        }
      />
      <div className="wdc-grid">
        <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
          <h2>Channels</h2>
          {p.channels.length === 0 ? (
            <p className="wd-muted">No linked channels.</p>
          ) : (
            <table className="wdc-table">
              <caption className="wd-sr-only">Linked channels (masked)</caption>
              <thead>
                <tr>
                  <th scope="col">Platform</th>
                  <th scope="col">Handle</th>
                  <th scope="col">Linked</th>
                </tr>
              </thead>
              <tbody>
                {p.channels.map((c) => (
                  <tr key={c.channelId}>
                    <td>
                      {humanizeStatus(c.platform)}
                      {c.primary ? ' (primary)' : ''}
                    </td>
                    <td>
                      <MaskedCell
                        masked={c.valueMasked}
                        subject={{
                          kind: 'PARTICIPANT_CONTACT',
                          participantId: p.participantId,
                          channelId: c.channelId,
                        }}
                        what={`${humanizeStatus(c.platform)} handle`}
                      />
                    </td>
                    <td>{intl.formatDateTime(c.linkedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <dl className="wdc-facts" style={{ marginTop: 'var(--wd-space-3)' }}>
            <div>
              <dt>Source platform</dt>
              <dd>{humanizeStatus(p.sourcePlatform ?? 'DIRECT')}</dd>
            </div>
          </dl>
        </section>
        <section className="wd-surface" style={{ padding: 'var(--wd-space-4)' }}>
          <h2>Consents (separable, §12.5)</h2>
          <dl className="wdc-facts">
            <div>
              <dt>Marketing</dt>
              <dd>
                {humanizeStatus(marketing)}
                {p.consents.marketingTextVersion ? ` · text v${p.consents.marketingTextVersion}` : ''}
                {p.consents.marketingRecordedAt
                  ? ` · ${intl.formatDateTime(p.consents.marketingRecordedAt)}`
                  : ''}
                {p.consents.marketingWithdrawnAt
                  ? ` · withdrawn ${intl.formatDateTime(p.consents.marketingWithdrawnAt)}`
                  : ''}
              </dd>
            </div>
            <div>
              <dt>Sponsor communication</dt>
              <dd>{p.consents.sponsorCommunication ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Winner publicity</dt>
              <dd>{p.consents.winnerPublicity ? 'Yes' : 'No (initials only by default)'}</dd>
            </div>
          </dl>
          <h3 style={{ marginTop: 'var(--wd-space-3)' }}>Risk flags</h3>
          {p.riskFlags.length === 0 ? (
            <p className="wd-muted">None. Flags are signals for review, never proof.</p>
          ) : (
            <ul>
              {p.riskFlags.map((f) => (
                <li key={`${f.code}-${f.at}`}>
                  {f.code} · {intl.formatDateTime(f.at)} · {f.reviewed ? 'reviewed' : 'open'}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <ConfirmDestructive
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        title="Record marketing withdrawal"
        objectName={p.participantId}
        objectKind="participant id"
        consequence="Future audience exports will exclude this contact. Operational winner messages are a separate purpose and remain unaffected."
        requireReason
        reasonLabel="How was the withdrawal received?"
        confirmLabel="Record withdrawal"
        busy={withdraw.isPending}
        error={withdraw.isError ? (withdraw.error as Error).message : null}
        onConfirm={({ reason }) => withdraw.mutate(reason ?? 'recorded by admin')}
      />
      <ConfirmDestructive
        open={eraseOpen}
        onOpenChange={setEraseOpen}
        title="Erase participant"
        objectName={p.participantId}
        objectKind="participant id"
        consequence="Profile, channel identities and identity media are erased. Legal holds block the erasure. A minimized suppression record is kept so the withdrawal stays effective. Requires step-up."
        requireReason
        reasonLabel="Reason (participant request, retention schedule…)"
        confirmLabel="Erase"
        busy={erase.isPending}
        error={
          erase.isError && !(erase.error instanceof StepUpCancelled) ? (erase.error as Error).message : null
        }
        onConfirm={({ reason }) => erase.mutate(reason ?? 'participant request')}
      />
    </>
  );
}
